import { Stack } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader';
import IconButton from '@components/IconButton';

import { useUpdateDocument } from '@hooks/api/document';
import { useGetStatuses } from '@hooks/api/status';
import { useDocumentViewer } from '@hooks/useDocumentViewer';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { GetDocumentDownloadUrlQuery } from 'generated/document.generated';
import { useGetDocumentDownloadUrlLazyQuery } from 'generated/document.generated';
import { DocumentHealthStatus, StatusType } from 'generated/types';

import type { ConfirmationDialogVariant } from '../ConfirmationDialog/ConfirmationDialog';

const messages = defineMessages({
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  ok: {
    id: 'GLOBAL.ok',
    defaultMessage: 'OK',
  },
  infectedTitle: {
    id: 'downloadButton.scanStatus.infected.title',
    defaultMessage: 'Download Blocked',
  },
  infectedSubtitle: {
    id: 'downloadButton.scanStatus.infected.subtitle',
    defaultMessage:
      "We've detected that this file is dangerous and could harm your computer.",
  },
  unknownTitle: {
    id: 'downloadButton.scanStatus.unknown.title',
    defaultMessage: 'Are you sure you want to {type} this file?',
  },
  unknownSubtitle: {
    id: 'downloadButton.scanStatus.unknown.subtitle',
    defaultMessage:
      'This file was unable to be scanned, it may contain dangerous content.',
  },
  inProgressTitle: {
    id: 'downloadButton.scanStatus.inProgress.title',
    defaultMessage: 'Are you sure you want to download this file?',
  },
  inProgressSubtitle: {
    id: 'downloadButton.scanStatus.inProgress.subtitle',
    defaultMessage: 'This file is still being scanned for dangerous content.',
  },
});

interface DownloadButtonProps {
  /**
   * The id of the document to download
   */
  document_id: string;
  /**
   * Type of button to render
   */
  type?: 'download' | 'view';
}

interface DialogProps {
  title: string;
  content: string;
  onConfirm: () => Promise<void>;
  variant: ConfirmationDialogVariant;
  open: boolean;
  showCancel?: boolean;
  cancelText?: string;
  showConfirm?: boolean;
  confirmText?: string;
}

const initialDialogProps: DialogProps = {
  title: '',
  content: '',
  onConfirm: () => Promise.resolve(),
  variant: 'info',
  open: false,
};

/**
 * Download button. Used to download and view documents
 */
export const DownloadButton: React.FC<DownloadButtonProps> = ({
  document_id,
  type = 'download',
}) => {
  const [dialogProps, setDialogProps] =
    React.useState<DialogProps>(initialDialogProps);
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { isClient, isCurator },
  } = useSession();
  const { viewDocument } = useDocumentViewer();

  const [downloadDocument, { loading: downloadLoading }] =
    useGetDocumentDownloadUrlLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [updateDocument] = useUpdateDocument();
  const { data: statusesData, loading } = useGetStatuses({
    model: StatusType.VitallPatient_RecordFiles,
    skip: isClient || type !== 'view',
  });
  const downloadedStatus = statusesData?.find(
    ({ public_name }) => public_name === 'Downloaded'
  );

  const handleDialogClose = () => {
    setDialogProps({ ...dialogProps, open: false });
  };

  /**
   * Fetches the document download URL and handles the response.
   *
   * @param {boolean} previewable - Whether the document is previewable.
   * @param {(queryResult: GetDocumentDownloadUrlQuery) => Promise<void>} onHealthy - The callback function to handle a healthy document.
   * @return {Promise<void>} A promise that resolves when the function completes.
   */
  const getDocument = async (
    previewable: boolean,
    onHealthy: (queryResult: GetDocumentDownloadUrlQuery) => Promise<void>
  ) => {
    // Fetch document download URL
    const response = await downloadDocument({
      variables: {
        input: {
          document_id,
        },
        previewable,
      },
    });

    // Extract query result from response
    const queryResult = response.data;

    // Extract document from query result
    const document = queryResult?.getDocumentRecord;

    // If document is not found, show error snackbar and return
    if (!document) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
      return;
    }

    // Extract download URL and status from document
    const downloadUrl = document.downloadUrl;
    const status = downloadUrl?.status;

    // Handle document status
    if (status === DocumentHealthStatus.Infected) {
      // Show error dialog for infected document
      setDialogProps({
        title: formatMessage(messages.infectedTitle),
        content: formatMessage(messages.infectedSubtitle),
        onConfirm: () => Promise.resolve(),
        variant: 'error',
        open: true,
        showConfirm: false,
        cancelText: formatMessage(messages.ok),
      });
    } else if (status === DocumentHealthStatus.InProgress) {
      // Show warning dialog for in-progress document
      setDialogProps({
        title: formatMessage(messages.inProgressTitle),
        content: formatMessage(messages.inProgressSubtitle),
        onConfirm: () => onHealthy(queryResult),
        variant: 'warning',
        open: true,
      });
    } else if (status === DocumentHealthStatus.Unknown) {
      // Show warning dialog for unknown document
      setDialogProps({
        title: formatMessage(messages.unknownTitle, { type }),
        content: formatMessage(messages.unknownSubtitle),
        onConfirm: () => onHealthy(queryResult),
        variant: 'warning',
        open: true,
      });
    } else if (status === DocumentHealthStatus.Ok) {
      // Call the onHealthy callback with the query result
      await onHealthy(queryResult);
    }
  };

  const handleDownload = async (queryResult: GetDocumentDownloadUrlQuery) => {
    try {
      const doc = queryResult.getDocumentRecord;
      if (!doc.downloadUrl?.url) {
        throw new Error('Unable to download file');
      }

      const { downloadUrl } = doc;
      const a = document.createElement('A') as HTMLAnchorElement;
      a.href = downloadUrl.url ?? '';
      a.download = '';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    } finally {
      // Only when curator downloads file should the status be changed
      if (isCurator && type === 'view') {
        await updateDocument({
          input: {
            document_id,
            document: {
              status_id: downloadedStatus?.status_id,
            },
          },
        });
      }
    }
  };

  const handleView = async (queryResult: GetDocumentDownloadUrlQuery) => {
    try {
      const doc = queryResult.getDocumentRecord;
      if (!doc.downloadUrl?.url) {
        throw new Error('Unable to view file');
      }
      viewDocument(doc);
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  return (
    <ContentLoader loading={loading}>
      <Stack spacing={3} direction="row">
        <ConfirmationDialog {...dialogProps} onClose={handleDialogClose}>
          {() => null}
        </ConfirmationDialog>
        {type === 'download' && (
          <IconButton
            disabled={downloadLoading}
            name="download"
            onClick={() => getDocument(false, handleDownload)}
            iconType="fas"
          />
        )}
        {type === 'view' && (
          <IconButton
            disabled={downloadLoading}
            name="eye"
            onClick={() => getDocument(true, handleView)}
            iconType="fas"
          />
        )}
      </Stack>
    </ContentLoader>
  );
};
